<template>
  <div class="appearances">
    <SelectionTiles
      v-slot="{ item }"
      :items="appearances"
      :item-key="
        (appearance: { id: number; name: string; color: string }) =>
          appearance.id.toString()
      "
      :item-disabled-text="
        (appearance: { id: number; name: string; color: string }) =>
          appearance.id === defaultAppearanceId
            ? $t(
                'PUBLISHING.DETAIL.PRODUCTS.DEACTIVATED_COLORS.DEFAULT_APPEARANCE_DISABLED_TEXT',
              )
            : ''
      "
      multi-selection
      :item-selected="
        (appearance: {
          id: number;
          name: string;
          color: string;
          active: boolean;
        }) => appearance.active
      "
      :item-to-string="
        (appearance: { id: number; name: string; color: string }) =>
          appearance.name
      "
      @change="toggleAppearanceActivation"
    >
      <ColorCircle :color="item.uiColor" :size="24" />
    </SelectionTiles>
    <div class="default-appearance">
      <strong>
        {{ $t('PUBLISHING.DETAIL.PRODUCTS.DEFAULT_COLOR.HEADING') }}
      </strong>
      <p>
        {{ $t('PUBLISHING.DETAIL.PRODUCTS.DEFAULT_COLOR.SUBHEADING') }}
      </p>
      <SelectionTiles
        v-if="activeAppearances && activeAppearances.length > 0"
        v-slot="{ item }"
        :items="activeAppearances"
        :item-key="
          (appearance: { id: number; name: string; color: string }) =>
            appearance.id.toString()
        "
        :item-selected="
          (appearance: { id: number; name: string; color: string }) =>
            appearance.id === props.defaultAppearanceId
        "
        :item-to-string="
          (appearance: { id: number; name: string; color: string }) =>
            appearance.name
        "
        @change="selectDefaultAppearance"
      >
        <ColorCircle v-if="item.uiColor" :color="item.uiColor" :size="24" />
        <span v-else>
          <Icon icon="palette" />
        </span>
      </SelectionTiles>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useStore } from 'vuex';
import SelectionTiles from 'src/app/components/selectionTiles/SelectionTiles.vue';
import ColorCircle from 'commons/colorCircle/ColorCircle.vue';
import Icon from 'commons/icon/component/Icon.vue';

interface Props {
  appearanceIds: number[];
  defaultAppearanceId: number;
  activeAppearanceIds: number[];
}

const store = useStore();
const props = defineProps<Props>();
const emits = defineEmits(['selectDefault', 'changeAppearanceActivation']);
const appearanceNames = computed(
  () => store.state.publishingCore.translations.appearances,
);
const appearanceColors = computed(() => store.state.publishingCore.appearances);

const appearances = computed(() => {
  if (!props.appearanceIds || !appearanceNames.value) {
    return [];
  }
  return props.appearanceIds
    .map((appearanceId) => ({
      id: appearanceId,
      name: (appearanceNames.value[appearanceId]?.name || '') as string,
      uiColor: appearanceColors.value.find(
        (appearance) => appearance.id === appearanceId,
      )?.colors,
      active: props.activeAppearanceIds.includes(appearanceId),
    }))
    .filter(
      (appearance) => appearance.uiColor && appearance.uiColor.length > 0,
    );
});
const activeAppearances = computed(() => {
  return appearances.value.filter((appearance) => appearance.active);
});

const selectDefaultAppearance = (appearance: { id: number }) => {
  emits('selectDefault', appearance.id);
};

const toggleAppearanceActivation = (appearance: {
  id: number;
  active: boolean;
}) => {
  emits('changeAppearanceActivation', appearance.id, !appearance.active);
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/styleguide/links';
@import 'src/scss/constants';

.default-appearance {
  margin-top: 24px;

  p {
    margin: 8px 0 16px 0;
    color: $grey65;
  }
}
</style>
